import { useState, useEffect } from 'react';
import { Heart, Check, X, Loader2, Users, Crown, Gift, Sparkles, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../lib/api-client';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Button } from './ui/button';
import { ProfileSidebarSections } from './ProfileSidebarSections';
import axios from 'axios';
import { SponsoredProfilesList } from './SponsoredProfilesList';
import { ViewAllSponsoredProfiles } from './ViewAllSponsoredProfiles';
import { logger } from '../utils/logger';

interface ActivityTabEnhancedProps {
  onViewProfile?: (profile: any, filterType?: string) => void;
  onNavigateToPlans?: () => void;
  onViewAllSuggested?: () => void;
}

export default function ActivityTabEnhanced({ onViewProfile, onNavigateToPlans, onViewAllSuggested }: ActivityTabEnhancedProps) {
  const [activeTab, setActiveTab] = useState<'received' | 'sent' | 'accepted' | 'shortlisted' | 'declined' | 'profile-views'>('received');
  const [shortlistedSubTab, setShortlistedSubTab] = useState<'my-shortlisted' | 'shortlisted-me'>('my-shortlisted');
  const [declinedSubTab, setDeclinedSubTab] = useState<'declined-by-me' | 'declined-by-others'>('declined-by-me');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [processing, setProcessing] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<'accept' | 'decline' | null>(null);
  const [suggestedProfiles, setSuggestedProfiles] = useState<any[]>([]);
  const [sponsoredProfiles, setSponsoredProfiles] = useState<any[]>([]);
  const [showSponsoredProfilesView, setShowSponsoredProfilesView] = useState(false);

  // Check for initial tab filter from sessionStorage
  useEffect(() => {
    const initialFilter = sessionStorage.getItem('activityTabFilter');
    if (initialFilter === 'shortlisted') {
      setActiveTab('shortlisted');
      // Clear the flag after using it
      sessionStorage.removeItem('activityTabFilter');
    }
  }, []);

  // Fetch suggested profiles
  useEffect(() => {
    fetchSuggestedProfiles();
    fetchSponsoredProfiles();
  }, []);

  const handleInviteNow = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.firstName || 'User';
    const referralCode = user.referralCode || user._id || 'ATOOTBANDAN';
    
    const shareText = `Join me on Atootbandan - India's trusted matrimonial platform! 💑\n\nFind your perfect match today.\nUse my referral code: ${referralCode}\n\nVisit: ${window.location.origin}`;
    
    // Check if Web Share API is available (mobile browsers)
    if (navigator.share) {
      navigator.share({
        title: 'Join Atootbandan',
        text: shareText,
        url: `${window.location.origin}?ref=${referralCode}`
      }).then(() => {
        toast.success('Thanks for sharing!');
      }).catch((error) => {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          // Fallback to copy
          copyToClipboard(shareText);
        }
      });
    } else {
      // Fallback: Copy to clipboard
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Invite link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy. Please try again.');
    });
  };

  const fetchSuggestedProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.log('⚠️ No token for suggested profiles');
        return;
      }

      logger.log('🔍 Fetching suggested profiles (ActivityTab)...');
      // Use the new suggested endpoint that filters by age +1/-1 year
      const response = await axios.get(`${API_URL}/profiles/suggested?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.log('📊 Suggested profiles response (ActivityTab):', response.data);

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        logger.log(`✅ Setting ${profilesArray.length} suggested profiles (ActivityTab)`);
        setSuggestedProfiles(profilesArray);
      } else {
        logger.log('⚠️ No suggested profiles data');
        if (response.data.message) {
          logger.log('Message from API:', response.data.message);
        }
        setSuggestedProfiles([]);
      }
    } catch (error) {
      logger.error('❌ Error fetching suggested profiles (ActivityTab):', error);
      if (error.response) {
        logger.error('Response error:', error.response.data);
      }
      setSuggestedProfiles([]);
    }
  };

  const fetchSponsoredProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Use the new featured endpoint that only returns premium members
      const response = await axios.get(`${API_URL}/profiles/featured?limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const profilesArray = response.data.data.profiles || [];
        setSponsoredProfiles(profilesArray);
      }
    } catch (error) {
      console.error('Error fetching featured profiles:', error);
      setSponsoredProfiles([]);
    }
  };

  // Fetch data based on active tab
  useEffect(() => {
    fetchTabData();
  }, [activeTab, shortlistedSubTab, declinedSubTab]);

  const fetchTabData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first');
        return;
      }

      let url = '';
      
      if (activeTab === 'received') {
        url = `${API_URL}/interactions/received?type=interest,superlike&status=pending`;
      } else if (activeTab === 'accepted') {
        // For accepted tab: show both received interests I accepted AND sent interests others accepted
        url = `${API_URL}/interactions/received?type=interest,superlike&status=accepted`;
      } else if (activeTab === 'sent') {
        // Only show pending interests in "Sent" tab
        url = `${API_URL}/interactions/sent?type=interest,superlike&status=pending`;
      } else if (activeTab === 'shortlisted') {
        if (shortlistedSubTab === 'my-shortlisted') {
          url = `${API_URL}/interactions/sent?type=shortlist`;
        } else if (shortlistedSubTab === 'shortlisted-me') {
          url = `${API_URL}/interactions/received?type=shortlist`;
        }
      } else if (activeTab === 'declined') {
        if (declinedSubTab === 'declined-by-me') {
          // Interests I received and I declined them
          url = `${API_URL}/interactions/received?type=interest,superlike&status=declined`;
        } else if (declinedSubTab === 'declined-by-others') {
          // Interests I sent and they declined them
          url = `${API_URL}/interactions/sent?type=interest,superlike&status=declined`;
        }
      } else if (activeTab === 'profile-views') {
        // Fetch profile views using the dedicated endpoint
        url = `${API_URL}/profile-views/received`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check if response is ok before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response has content
      const text = await response.text();
      if (!text) {
        logger.error('Empty response from API');
        setData([]);
        return;
      }

      const result = JSON.parse(text);
      
      if (result.success) {
        let items = result.data?.interactions || result.data?.matches || result.data?.views || result.data || [];
        
        // For sent tab, filter out accepted/declined interests (they should only appear in Accepted tab)
        if (activeTab === 'sent') {
          items = (Array.isArray(items) ? items : []).filter((item: any) => 
            item.status !== 'accepted' && item.status !== 'declined'
          );
        }
        
        // For profile-views, map the data to match the format
        if (activeTab === 'profile-views') {
          items = (Array.isArray(items) ? items : []).map((view: any) => ({
            _id: view._id,
            fromProfile: view.viewerProfile || view.viewer,
            fromUser: view.viewer,
            lastViewedAt: view.lastViewedAt,
            viewCount: view.viewCount
          }));
        }
        
        // For accepted tab, also fetch sent interests that were accepted by others
        if (activeTab === 'accepted') {
          // Mark received accepted interests with source
          items = (Array.isArray(items) ? items : []).map((item: any) => ({
            ...item,
            _interactionSource: 'received'
          }));
          
          const sentResponse = await fetch(`${API_URL}/interactions/sent?type=interest,superlike`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          const sentResult = await sentResponse.json();
          
          if (sentResult.success) {
            const sentItems = sentResult.data?.interactions || sentResult.data?.matches || sentResult.data || [];
            // ONLY include sent interests that have been ACCEPTED (status === 'accepted')
            const acceptedSentItems = (Array.isArray(sentItems) ? sentItems : [])
              .filter((item: any) => item.status === 'accepted')
              .map((item: any) => ({
                ...item,
                _interactionSource: 'sent'
              }));
            // Combine both arrays
            items = [...items, ...acceptedSentItems];
          }
        }
        
        setData(Array.isArray(items) ? items : []);
      } else {
        setData([]);
        toast.error(result.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('Error:', error);
      setData([]);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (interestId: string) => {
    setProcessing(interestId);
    setProcessingAction('accept');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/interactions/accept/${interestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Interest accepted!');
        fetchTabData();
      } else {
        toast.error(result.message || 'Failed to accept interest');
      }
    } catch (error) {
      toast.error('Failed to accept interest');
    } finally {
      setProcessing(null);
      setProcessingAction(null);
    }
  };

  const handleDecline = async (interestId: string) => {
    setProcessing(interestId);
    setProcessingAction('decline');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/interactions/decline/${interestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Interest declined');
        fetchTabData();
      } else {
        toast.error(result.message || 'Failed to decline interest');
      }
    } catch (error) {
      toast.error('Failed to decline interest');
    } finally {
      setProcessing(null);
      setProcessingAction(null);
    }
  };

  const handleCancel = async (interestId: string) => {
    setProcessing(interestId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/interactions/${interestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Interest cancelled');
        fetchTabData();
      } else {
        toast.error(result.message || 'Failed to cancel interest');
      }
    } catch (error) {
      toast.error('Failed to cancel interest');
    } finally {
      setProcessing(null);
    }
  };

  const renderProfileCard = (item: any) => {
    // Determine which profile to use based on the tab and interaction source
    let profile: any;
    let user: any;
    
    if (activeTab === 'sent') {
      // For sent interests: show the receiver's profile
      profile = item.toProfile || item.toUserProfile || {};
      user = item.toUser || {};
    } else if (activeTab === 'received') {
      // For received interests: show the sender's profile
      profile = item.fromProfile || item.fromUserProfile || {};
      user = item.fromUser || {};
    } else if (activeTab === 'accepted') {
      // For accepted tab: check the interaction source
      if (item._interactionSource === 'sent') {
        // This was an interest I sent and they accepted - show their profile
        profile = item.toProfile || item.toUserProfile || {};
        user = item.toUser || {};
      } else {
        // This was an interest I received and accepted - show their profile
        profile = item.fromProfile || item.fromUserProfile || {};
        user = item.fromUser || {};
      }
    } else if (activeTab === 'shortlisted') {
      if (shortlistedSubTab === 'my-shortlisted') {
        profile = item.toProfile || item.toUserProfile || {};
        user = item.toUser || {};
      } else {
        profile = item.fromProfile || item.fromUserProfile || {};
        user = item.fromUser || {};
      }
    } else if (activeTab === 'declined') {
      // For declined, check if it's declined-by-me or declined-by-others
      if (declinedSubTab === 'declined-by-me') {
        // I received and declined - show the sender's profile
        profile = item.fromProfile || item.fromUserProfile || {};
        user = item.fromUser || {};
      } else {
        // They declined my sent interest - show the receiver's profile
        profile = item.toProfile || item.toUserProfile || {};
        user = item.toUser || {};
      }
    } else if (activeTab === 'profile-views') {
      // For profile views, show the sender's profile
      profile = item.fromProfile || item.fromUserProfile || {};
      user = item.fromUser || {};
    } else {
      profile = item.fromProfile || item.toProfile || item.fromUserProfile || item.toUserProfile || {};
      user = item.fromUser || item.toUser || {};
    }
    
    const firstName = user.firstName || profile.userId?.firstName || profile.firstName || 'User';
    const lastName = user.lastName || profile.userId?.lastName || profile.lastName || '';
    
    // Handle name display based on privacy settings
    const showFullName = user.preferences?.showFullName !== false && profile.userId?.preferences?.showFullName !== false;
    const displayName = showFullName 
      ? `${firstName} ${lastName}` 
      : `${firstName} ${lastName ? lastName.charAt(0) + '.' : ''}`;
    
    const age = profile.age || profile.basicInfo?.age || 26;
    const height = profile.height || profile.basicInfo?.height || "5'5\"";
    const occupation = profile.occupation || profile.career?.occupation || 'Professional';
    const location = profile.location?.city || profile.city || 'India';
    
    // Extract photo correctly from the profile
    let photo = null;
    if (profile.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
      const firstPhoto = profile.photos[0];
      if (typeof firstPhoto === 'object' && firstPhoto?.url) {
        photo = firstPhoto.url;
      } else if (typeof firstPhoto === 'string') {
        photo = firstPhoto;
      }
    }
    if (!photo) {
      photo = profile.profilePhoto || user.profilePhoto || profile.userId?.profilePhoto;
    }
    // Also check user object photos array
    if (!photo && user.photos && Array.isArray(user.photos) && user.photos.length > 0) {
      const userPhoto = user.photos[0];
      if (typeof userPhoto === 'object' && userPhoto?.url) {
        photo = userPhoto.url;
      } else if (typeof userPhoto === 'string') {
        photo = userPhoto;
      }
    }
    
    // Remove ui-avatars.com fallback - let ImageWithFallback handle it with initials
    const fallbackPhoto = null;

    // Determine the correct profile to view based on the tab and interaction source
    const getProfileToView = () => {
      if (activeTab === 'sent') {
        // Viewing profiles you sent interests to
        return item.toProfile || item.toProfileId || item.toUserProfile || profile;
      } else if (activeTab === 'received') {
        // Viewing profiles who sent interests to you
        return item.fromProfile || item.fromUserProfile || item.fromProfileId || profile;
      } else if (activeTab === 'accepted') {
        // For accepted, check interaction source
        if (item._interactionSource === 'sent') {
          return item.toProfile || item.toProfileId || item.toUserProfile || profile;
        } else {
          return item.fromProfile || item.fromUserProfile || item.fromProfileId || profile;
        }
      } else if (activeTab === 'shortlisted') {
        // For shortlisted, check if it's my-shortlisted or shortlisted-me
        if (shortlistedSubTab === 'my-shortlisted') {
          return item.toProfile || item.toProfileId || item.toUserProfile || profile;
        } else {
          return item.fromProfile || item.fromUserProfile || item.fromProfileId || profile;
        }
      } else if (activeTab === 'declined') {
        // For declined, check if it's declined-by-me or declined-by-others
        if (declinedSubTab === 'declined-by-me') {
          return item.fromProfile || item.fromProfileId || item.fromUserProfile || profile;
        } else {
          return item.toProfile || item.toProfileId || item.toUserProfile || profile;
        }
      } else if (activeTab === 'profile-views') {
        // For profile views, show the sender's profile
        return item.fromProfile || item.fromProfileId || item.fromUserProfile || profile;
      }
      return profile;
    };

    const profileToView = getProfileToView();

    return (
      <div
        key={item._id}
        className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4 hover:shadow-md transition-shadow"
      >
        {/* Photo */}
        <div 
          className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
          onClick={() => onViewProfile?.(profileToView, activeTab)}
        >
          <ImageWithFallback
            src={photo || fallbackPhoto}
            alt={firstName}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 truncate">
            {displayName}
          </h3>
          <p className="text-sm text-gray-600">
            {age} yrs, {height} • {occupation}
          </p>
          <p className="text-xs text-gray-500">{location}</p>

          {/* Action Buttons */}
          {activeTab === 'received' && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => handleAccept(item._id)}
                disabled={processing === item._id}
                className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {processing === item._id && processingAction === 'accept' ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <Check className="size-4" />
                    Accept
                  </>
                )}
              </button>
              <button
                onClick={() => handleDecline(item._id)}
                disabled={processing === item._id}
                className="flex items-center gap-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {processing === item._id && processingAction === 'decline' ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Declining...
                  </>
                ) : (
                  <>
                    <X className="size-4" />
                    Decline
                  </>
                )}
              </button>
            </div>
          )}

          {/* Cancel button for Sent tab */}
          {activeTab === 'sent' && (
            <button
              onClick={() => handleCancel(item._id)}
              disabled={processing === item._id}
              className="flex items-center gap-1 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition-colors mt-2"
            >
              {processing === item._id ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <X className="size-4" />
              )}
              Cancel Interest
            </button>
          )}

          {/* View Profile button for other tabs */}
          {activeTab !== 'received' && activeTab !== 'sent' && (
            <button
              onClick={() => onViewProfile?.(profileToView, activeTab)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors mt-2"
            >
              View Profile
            </button>
          )}
        </div>
      </div>
    );
  };

  // Show sponsored profiles view if requested
  if (showSponsoredProfilesView) {
    return (
      <ViewAllSponsoredProfiles
        onBack={() => setShowSponsoredProfilesView(false)}
        onViewProfile={onViewProfile}
      />
    );
  }

  return (
    <div className="h-full bg-gray-50 flex">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white">
          {/* Tabs Header */}
          <div className="border-b border-gray-200 px-6">
            <div className="flex gap-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('received')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'received' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Received
                {activeTab === 'received' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('sent')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'sent' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sent
                {activeTab === 'sent' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('accepted')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'accepted' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Accepted
                {activeTab === 'accepted' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('shortlisted')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'shortlisted' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Shortlisted
                {activeTab === 'shortlisted' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('declined')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'declined' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Declined
                {activeTab === 'declined' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>

              <button
                onClick={() => setActiveTab('profile-views')}
                className={`relative flex items-center gap-2 py-4 px-2 font-semibold transition-colors whitespace-nowrap ${
                  activeTab === 'profile-views' ? 'text-pink-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Profile Views
                {activeTab === 'profile-views' && (
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-pink-600 rounded-t-full" />
                )}
              </button>
            </div>

            {/* Shortlisted Sub-Tabs */}
            {activeTab === 'shortlisted' && (
              <div className="flex gap-2 border-t border-gray-200 bg-gray-50 px-4">
                <button
                  onClick={() => setShortlistedSubTab('my-shortlisted')}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    shortlistedSubTab === 'my-shortlisted'
                      ? 'text-pink-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  My Shortlisted
                  {shortlistedSubTab === 'my-shortlisted' && (
                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-pink-600" />
                  )}
                </button>
                <button
                  onClick={() => setShortlistedSubTab('shortlisted-me')}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    shortlistedSubTab === 'shortlisted-me'
                      ? 'text-pink-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Shortlisted Me
                  {shortlistedSubTab === 'shortlisted-me' && (
                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-pink-600" />
                  )}
                </button>
              </div>
            )}

            {/* Declined Sub-Tabs */}
            {activeTab === 'declined' && (
              <div className="flex gap-2 border-t border-gray-200 bg-gray-50 px-4">
                <button
                  onClick={() => setDeclinedSubTab('declined-by-me')}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    declinedSubTab === 'declined-by-me'
                      ? 'text-pink-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Declined by Me
                  {declinedSubTab === 'declined-by-me' && (
                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-pink-600" />
                  )}
                </button>
                <button
                  onClick={() => setDeclinedSubTab('declined-by-others')}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    declinedSubTab === 'declined-by-others'
                      ? 'text-pink-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Declined by Others
                  {declinedSubTab === 'declined-by-others' && (
                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-pink-600" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Sponsored Profiles Section - Show at top */}
          <div className="mb-6">
            <SponsoredProfilesList
              onViewProfile={onViewProfile}
              onViewAll={() => setShowSponsoredProfilesView(true)}
              maxProfiles={4}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="size-8 animate-spin text-pink-500" />
            </div>
          ) : data.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12">
              <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Heart className="size-12 text-gray-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Found</h3>
                <p className="text-gray-500 mb-6">
                  {activeTab === 'received' && "You haven't received any interests yet"}
                  {activeTab === 'sent' && "You haven't sent any interests yet"}
                  {activeTab === 'accepted' && "No accepted interests yet"}
                  {activeTab === 'shortlisted' && "No shortlisted profiles yet"}
                  {activeTab === 'declined' && "No declined interests yet"}
                  {activeTab === 'profile-views' && "No profile views yet"}
                </p>
                <Button className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white">
                  <Heart className="mr-2 h-4 w-4" />
                  Explore Matches
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              {data.map((item) => renderProfileCard(item))}
            </div>
          )}

          {/* Bottom Section - Informational Cards */}
          {!loading && data.length > 0 && (
            <>
              {/* Suggested Profiles - Horizontal List */}


              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Refer & Earn */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="h-5 w-5 text-blue-600" />
                    <h3 className="font-bold text-gray-900">Refer & Earn</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Invite friends and earn rewards for every successful match!
                  </p>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm" onClick={handleInviteNow}>
                    Invite Now
                  </Button>
                </div>

                {/* Trust & Safety */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Why Trust Us?</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <p className="text-sm text-gray-700">100% Verified Profiles</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <p className="text-sm text-gray-700">10,000+ Success Stories</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Sparkles className="h-4 w-4 text-purple-600" />
                      </div>
                      <p className="text-sm text-gray-700">Premium Privacy Controls</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Sidebar - Desktop Only - Streamlined */}
      <aside className="hidden xl:block w-80 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
        <div className="p-4 space-y-6">
          {/* Premium Banner */}
          <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 rounded-2xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
            <div className="relative z-10">
              <Crown className="h-10 w-10 mb-3 text-yellow-300" />
              <h3 className="font-bold text-lg mb-2">Upgrade to Premium</h3>
              <p className="text-sm text-white/90 mb-4">
                Get 3x more matches and unlock unlimited messaging
              </p>
              <Button className="w-full bg-white text-orange-600 hover:bg-gray-50 font-semibold" onClick={onNavigateToPlans}>
                View Plans
              </Button>
            </div>
          </div>

          {/* Suggested & Featured Profiles */}
          <ProfileSidebarSections 
            suggestedProfiles={suggestedProfiles}
            featuredProfiles={sponsoredProfiles}
            onViewProfile={onViewProfile}
          />
        </div>
      </aside>
    </div>
  );
}